/**
 * Feature flag keys for LaunchDarkly
 * Use these constants with useFeatureFlag hook from @vizit/feature-flags
 *
 * @example
 * import { useFeatureFlag } from '@vizit/feature-flags';
 * import { FEATURE_FLAGS } from '@vizit/constants';
 *
 * const isAprilEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);
 */
export const FEATURE_FLAGS = {
  /** Controls redirect from /login to /sso when SSO flow is enabled */
  SSO_LOGIN_FLOW: 'sso-login-flow',
  /** Product details page v2 with improved UI layout */
  PDP_V2: 'pdp-v-2',
  /** V2 metric cells in the Product Page Performance section */
  PDP_V2_METRICS: 'pdp-v-2-metrics',
  /** Formula Scorecard on the PDP performance section (weights disclosure, normalized cells, scoring-formula panel) when the PDP carries an AVAILABLE score breakdown. Off = today's GAM UI. Per-org opt-in, v2-launch style. */
  FORMULA_SCORECARD: 'formula-scorecard',
  /** Temporary clip experiment - disables Spark and Explore functionality */
  TMP_CLIP_EXPERIMENT: 'tmp-clip-experiment',
  /** Enables real Spark Images generation on hero images (AI-generated image variations). When off, hero Spark Images tab shows a "coming soon" card. Independent of HERO_SPARK_IDEAS. */
  HERO_SPARK_IMAGES: 'hero-spark-images',
  /** Enables real Spark Ideas generation on hero images (markdown idea content). When off, hero Spark Ideas tab shows a "coming soon" card. Independent of HERO_SPARK_IMAGES. */
  HERO_SPARK_IDEAS: 'hero-spark-ideas',
  /** Controls visibility of image classification labels on product assets */
  SHOW_IMAGE_CLASSIFICATION: 'show-image-classification',
  /** Master April release flag — gates retailer, scoring, spark images, and all April features */
  APRIL_SCORING_CHANGES: 'april-scoring-changes',
  /** Gates the Mobile Readiness panel on the hero score tab, independent of the master April flag */
  MOBILE_READINESS_ENABLED: 'mobile-readiness-enabled',
  /** APP-131: enables spark cull integration (t=0 skeletons, PENDING_REVIEW rendering, stale-pending filter) */
  FEAT_SPARK_CULL: 'feat-spark-cull',
  /** SPM-2834: per-org hero scoring formula. String variations: "v2_4_component" (default) | "legacy_3_component" (Blue Buffalo carve-out). */
  HERO_SCORE_FORMULA: 'hero-score-formula',
  /** SPM-2892: gates the asset class edit affordance and downstream mutation wiring on the PDP / Asset Details Page. */
  FEAT_CHANGE_ASSET_CLASS: 'feat-change-asset-class',
  /** Gates the Integrations entry point in the user menu and the /settings/integrations route. */
  INTEGRATIONS: 'integrations',
  /** Gates the Opportunity Reporting page at /reports/opportunity. */
  OPPORTUNITY_REPORT: 'opportunity-report',
  /** Gates the new GS1-based Hero Scoring panel on the asset hero-score tab. Pairs with the BE flag `hero-gs1-scoring-be` — both must be on, plus `hero_gs1_components` present on the image, for the new panel to render. */
  HERO_GS1_SCORING_FE: 'hero-gs1-scoring-fe',
  /** Temporarily hides the "Hero" choice from the image-type pickers in the Score an Image and Create a Collection flows. Flip on while the new hero scoring is live but those flows are missing the required product metadata; flip off once they support it again. */
  HIDE_HERO_IN_IMAGE_FLOWS: 'hide-hero-in-image-flows',
  /** GRE-113: hides Vizit scores throughout Explore — the score bars under asset thumbnails and the radial/pill score in the image detail modal. UI-only visibility toggle; does not affect scoring elsewhere. */
  HIDE_EXPLORE_SCORES: 'hide-explore-scores',
  /** SPM-3088: per-org flag to show the raw scraped L4 category name (PdpView.scraped_category_name) on PDPs in place of the mapped canonical category. Falls back to the canonical category when off or when no scraped name is available. */
  SHOW_SCRAPED_L4_CATEGORY: 'show-scraped-l4-category',
  /** GRE-285: gates the bulk-invite entry point on the User Management page — turns the single Invite button into a menu with a "Bulk invite" option. */
  BULK_ADD_USERS: 'bulk-add-users',
  /** SPM-3405: shows the favourites surfaces — the heart toggle on asset tiles and the favorites-only filters. Off hides them; stored favourites are untouched either way and reappear when it is turned back on. */
  SHOW_FAVORITES: 'show-favorites',
  /** SPM-3405: shows the Spark-chain thumbnail breadcrumbs on the asset details page. When off, `useSparkChainBreadcrumbs` returns undefined and the page falls back to the route loader's plain text breadcrumbs. */
  SHOW_SPARK_BREADCRUMBS: 'show-spark-breadcrumbs',
  /** SPM-3405: shows the GenAI badge overlaid on AI-generated asset thumbnails. Visibility only — `isAiGenerated` still drives everything else. */
  SHOW_GEN_AI_BADGE: 'show-gen-ai-badge',
  /** SPM-3405: shows the delve-in navigation between an image and its generations — the "Back to source image" control in the artifact viewer, pressing a Spark tile to open the generated image's own page, and the Spark images tab on that page. */
  SHOW_IMAGE_NAVIGATION: 'show-image-navigation',
  /** Shows the Asset Details Export menu (creative brief PDF and image ZIP). Shares its key with the API flag that gates the ZIP endpoint; target it by organizationId so both sides agree. */
  SPARK_ASSET_EXPORT: 'spark-asset-export',
  /** GRE-358: shows the prompt box on the Spark Images tab so a generation can be steered with free text. Shares its key with the API flag that decides whether a sent prompt is honored. Target it by organizationId: the API evaluates with a constant context key, so a percentage rollout would split the two sides. */
  SPARK_IMAGE_PROMPTS: 'spark-image-prompts',
  /** Gates custom benchmarks: the Collections / Benchmarks switch on the Library Collections tab and every benchmark surface behind it. Off leaves the Collections tab exactly as it was. Pairs with the BE flag `custom-benchmarks`, which gates the benchmark endpoints; turn both on for an organization. */
  CUSTOM_BENCHMARKS_FE: 'custom-benchmarks-fe',
  /** GRE-428: shows the Plain support chat launcher in the signed-in app. Also needs VITE_PLAIN_APP_ID in the environment. */
  PLAIN_CHAT: 'plain-chat',
  /** BLU-343: Classic to Next Gen Actions Bar PRD (thumbnail size slider, multi-select action bar, bulk download). Only hides unfinished work, the release is for everyone at once. */
  ACTIONS_BAR_FE: 'actions-bar-fe',
} as const;

/** Variation values for the HERO_SCORE_FORMULA flag. */
export const HERO_SCORE_FORMULA_VALUES = {
  V2_4_COMPONENT: 'v2_4_component',
  LEGACY_3_COMPONENT: 'legacy_3_component',
} as const;

export type HeroScoreFormulaValue = (typeof HERO_SCORE_FORMULA_VALUES)[keyof typeof HERO_SCORE_FORMULA_VALUES];

/**
 * Default feature flag values (used when LaunchDarkly is not available)
 * These match the keys in FEATURE_FLAGS
 */
export const FEATURE_FLAG_DEFAULTS = {
  [FEATURE_FLAGS.SSO_LOGIN_FLOW]: false,
  [FEATURE_FLAGS.PDP_V2]: false,
  [FEATURE_FLAGS.PDP_V2_METRICS]: false,
  [FEATURE_FLAGS.FORMULA_SCORECARD]: false,
  [FEATURE_FLAGS.TMP_CLIP_EXPERIMENT]: false,
  [FEATURE_FLAGS.HERO_SPARK_IMAGES]: false,
  [FEATURE_FLAGS.HERO_SPARK_IDEAS]: false,
  [FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION]: false,
  [FEATURE_FLAGS.APRIL_SCORING_CHANGES]: false,
  [FEATURE_FLAGS.MOBILE_READINESS_ENABLED]: false,
  [FEATURE_FLAGS.FEAT_SPARK_CULL]: false,
  [FEATURE_FLAGS.HERO_SCORE_FORMULA]: HERO_SCORE_FORMULA_VALUES.V2_4_COMPONENT,
  [FEATURE_FLAGS.FEAT_CHANGE_ASSET_CLASS]: false,
  [FEATURE_FLAGS.INTEGRATIONS]: false,
  [FEATURE_FLAGS.OPPORTUNITY_REPORT]: false,
  [FEATURE_FLAGS.HERO_GS1_SCORING_FE]: false,
  [FEATURE_FLAGS.HIDE_HERO_IN_IMAGE_FLOWS]: false,
  [FEATURE_FLAGS.HIDE_EXPLORE_SCORES]: false,
  [FEATURE_FLAGS.SHOW_SCRAPED_L4_CATEGORY]: false,
  [FEATURE_FLAGS.BULK_ADD_USERS]: false,
  [FEATURE_FLAGS.SHOW_FAVORITES]: false,
  [FEATURE_FLAGS.SHOW_SPARK_BREADCRUMBS]: false,
  [FEATURE_FLAGS.SHOW_GEN_AI_BADGE]: false,
  [FEATURE_FLAGS.SHOW_IMAGE_NAVIGATION]: false,
  [FEATURE_FLAGS.SPARK_ASSET_EXPORT]: false,
  [FEATURE_FLAGS.SPARK_IMAGE_PROMPTS]: false,
  [FEATURE_FLAGS.CUSTOM_BENCHMARKS_FE]: false,
  [FEATURE_FLAGS.PLAIN_CHAT]: false,
  [FEATURE_FLAGS.ACTIONS_BAR_FE]: false,
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * Human-readable descriptions for every flag key, in the words an internal
 * user should see (the Org Management flag panel renders them). Typed over
 * every key so adding a flag without a description is a type error.
 */
export const FEATURE_FLAG_DESCRIPTIONS: Record<FeatureFlagKey, string> = {
  [FEATURE_FLAGS.SSO_LOGIN_FLOW]: 'Redirects /login to the SSO flow.',
  [FEATURE_FLAGS.PDP_V2]: 'Product details page v2 layout, including content mix.',
  [FEATURE_FLAGS.PDP_V2_METRICS]: 'V2 metric cells in the Product Page Performance section.',
  [FEATURE_FLAGS.FORMULA_SCORECARD]:
    'Frontend switch for the formula scorecard on the PDP performance section (weights, normalized cells, scoring-formula panel). Off shows the GAM UI.',
  [FEATURE_FLAGS.TMP_CLIP_EXPERIMENT]: 'Temporary CLIP experiment. Disables Spark and Explore navigation.',
  [FEATURE_FLAGS.HERO_SPARK_IMAGES]: 'Real Spark Images generation on hero images. Off shows a coming-soon card.',
  [FEATURE_FLAGS.HERO_SPARK_IDEAS]: 'Real Spark Ideas generation on hero images. Off shows a coming-soon card.',
  [FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION]: 'Image classification labels on product assets.',
  [FEATURE_FLAGS.APRIL_SCORING_CHANGES]:
    'Master April release flag. Gates retailer, scoring, Spark images, and all April features.',
  [FEATURE_FLAGS.MOBILE_READINESS_ENABLED]:
    'Mobile Readiness panel on the hero score tab, independent of the April flag.',
  [FEATURE_FLAGS.FEAT_SPARK_CULL]:
    'Spark cull integration: t=0 skeletons, pending-review rendering, stale-pending filter.',
  [FEATURE_FLAGS.HERO_SCORE_FORMULA]:
    'Per-org hero scoring formula. Variations: v2_4_component (default, 4-component) | legacy_3_component (Blue Buffalo carve-out).',
  [FEATURE_FLAGS.FEAT_CHANGE_ASSET_CLASS]: 'Asset class edit affordance on the PDP and Asset Details page.',
  [FEATURE_FLAGS.INTEGRATIONS]:
    'Integrations entry point in the user menu and the /settings/integrations route. Required for Opportunity Reporting.',
  [FEATURE_FLAGS.OPPORTUNITY_REPORT]: 'Opportunity Reporting page at /reports/opportunity.',
  [FEATURE_FLAGS.HERO_GS1_SCORING_FE]:
    'GS1-based Hero Scoring panel on the hero-score tab. Pairs with the backend flag hero-gs1-scoring-be.',
  [FEATURE_FLAGS.HIDE_HERO_IN_IMAGE_FLOWS]:
    'Hides the Hero choice from the image-type pickers in Score an Image and Create a Collection.',
  [FEATURE_FLAGS.HIDE_EXPLORE_SCORES]: 'Hides Vizit scores throughout Explore (score bars and the modal score).',
  [FEATURE_FLAGS.SHOW_SCRAPED_L4_CATEGORY]:
    'Shows the raw scraped L4 category name on PDPs instead of the mapped canonical category.',
  [FEATURE_FLAGS.BULK_ADD_USERS]: 'Bulk invite (CSV upload) entry point on the User Management page.',
  [FEATURE_FLAGS.SHOW_FAVORITES]: 'Heart toggle on asset tiles and favorites-only filters.',
  [FEATURE_FLAGS.SHOW_SPARK_BREADCRUMBS]: 'Spark-chain thumbnail breadcrumbs on the asset details page.',
  [FEATURE_FLAGS.SHOW_GEN_AI_BADGE]: 'GenAI badge overlaid on AI-generated asset thumbnails.',
  [FEATURE_FLAGS.SHOW_IMAGE_NAVIGATION]:
    'Navigation between an image and its generations: Back to source image, opening the page of a Spark tile, and the Spark images tab on that page.',
  [FEATURE_FLAGS.SPARK_ASSET_EXPORT]:
    'Asset Details export menu (creative brief PDF and image ZIP). Shares its key with the API flag that gates the ZIP endpoint.',
  [FEATURE_FLAGS.SPARK_IMAGE_PROMPTS]:
    'Prompt box on the Spark Images tab. Shares its key with the API flag that decides whether a sent prompt is honored.',
  [FEATURE_FLAGS.CUSTOM_BENCHMARKS_FE]:
    'Collections / Benchmarks switch on the Library Collections tab and the benchmark views behind it. Pairs with the backend flag custom-benchmarks.',
  [FEATURE_FLAGS.PLAIN_CHAT]: 'Plain support chat launcher in the signed-in app. Also needs VITE_PLAIN_APP_ID set.',
  [FEATURE_FLAGS.ACTIONS_BAR_FE]:
    'Classic to Next Gen Actions Bar: thumbnail size slider, multi-select action bar and bulk download in Collections.',
};
